import { Typography, Stack, Container } from '@mui/material';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';
import { decode } from 'base-64';
import { base64url, compactDecrypt } from 'jose';
import moment from 'moment-timezone';
import LoadingSpinner from '../components/LoadingSpinner';
import PageHeader from '../components/PageHeader';
import PatientCard from '../components/PatientCard';
import networkConfig from '../config/networkConfig';
import { PSContext, SHLContext, ShlLinkContext } from '../Contexts';
import { safeFetch } from '../utils/dataUtil';
import { ErrorTypes } from '../constants';
import { VSError } from '../model/error';
import PageLayout from './PageLayout';
import PageFooter from '../components/PageFooter';
import { DateFormats, formatDate } from '../utils/momentUtil';

/**
 * Layout for Patient Summary landing and section pages. Fetches and provies PS data.
 * @returns {React.FunctionComponent} React element that contains the layout.
 */
function PSLayout() {
  const { files, encryptionKey } = useContext(SHLContext);
  const [translate, i18n] = useTranslation();

  const [isLoading, setIsLoading] = useState(true);
  const [ps, setPS] = useState(null);

  const { showBoundary } = useErrorBoundary();

  const shl = useContext(ShlLinkContext);
  const expiryDate = moment.unix(JSON.parse(decode(shl)).exp);

  useEffect(() => {
    const abortController = new AbortController();
    const fetchData = async () => {
      files.forEach(async (file) => {
        if (file.contentType === 'application/fhir+json') {
          try {
            const config = {
              method: 'GET',
              headers: {
                'X-VSF-API-TOKEN': networkConfig.apiKey,
                'Accept-Language': i18n.language,
              },
              signal: abortController.signal,
            };

            const response = await safeFetch(
              file.location,
              config,
              showBoundary,
            );
            const responseJSON = await response?.json();

            if (responseJSON) {
              const { plaintext } = await compactDecrypt(
                responseJSON.data,
                base64url.decode(encryptionKey),
              );
              const json = JSON.parse(new TextDecoder().decode(plaintext));
              if (json) {
                setInterval(() => {
                  if (moment().isSameOrAfter(expiryDate, 'second')) {
                    showBoundary(
                      new VSError(
                        ErrorTypes.EXPIRED,
                        '',
                        '',
                        'FHIR_EXP',
                        'FHIR',
                      ),
                    );
                  }
                }, 5000);
                setPS(json);
              }
            }
          } finally {
            setIsLoading(false);
          }
        }
      });
    };

    fetchData();

    return () => {
      abortController.abort();
    };
  }, []);

  const expiryFooter = (
    <PageFooter>
      <Typography
        variant="body_xs"
        textAlign="center"
        sx={{ color: 'text.secondary' }}
      >
        {translate('patientGenerated', {
          date: formatDate(expiryDate, DateFormats.YEAR_MONTH_DAY_TIME),
        })}
      </Typography>
    </PageFooter>
  );

  return (
    <PSContext.Provider value={useMemo(() => ({ ps, shl }), [ps, shl])}>
      <PageLayout
        header={<PageHeader homeURL="/" ps={ps} />}
        footer={expiryFooter}
      >
        <Container
          maxWidth={false}
          disableGutters
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {(isLoading || !ps) && <LoadingSpinner />}
          {!isLoading && ps && (
            <Stack
              direction="column"
              alignItems="flex-start"
              padding={4}
              gap={2}
              sx={{ flexGrow: 1 }}
            >
              <PatientCard />
              <Outlet />
            </Stack>
          )}
        </Container>
      </PageLayout>
    </PSContext.Provider>
  );
}

export default PSLayout;
